import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { extractApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  ArrowLeft, FileEdit, Eye, Upload, Undo2, Trash2, Plus, RefreshCw,
  ShieldAlert, CheckCircle2, XCircle,
} from "lucide-react";

const HONEST_NOTE = (
  "Course content lives in the course_content collection. This page is " +
  "the source of truth for admins to draft, edit, publish, unpublish, " +
  "and restore versions. Participants only see PUBLISHED versions; " +
  "existing completions keep the version they completed against."
);

function StateBadge({ state }) {
  const cls = state === "published"
    ? "bg-progress-teal/15 text-progress-teal border-progress-teal/40"
    : state === "draft"
    ? "bg-accent-warm/15 text-accent-warm border-accent-warm/40"
    : "bg-fg-secondary/10 text-fg-secondary border-border-subtle";
  return (
    <span
      data-testid={`content-state-badge-${state}`}
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider ${cls}`}
    >{state}</span>
  );
}

export default function AdminContent() {
  const [params, setParams] = useSearchParams();
  const selectedVersion = params.get("v");
  const [versions, setVersions] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [coursesJSON, setCoursesJSON] = useState("");
  const [fieldsJSON, setFieldsJSON] = useState("");
  const [jsonError, setJsonError] = useState(null);
  const [serverErrors, setServerErrors] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [previewModuleId, setPreviewModuleId] = useState("");
  const [previewData, setPreviewData] = useState(null);
  const [notes, setNotes] = useState("");
  const [diffData, setDiffData] = useState(null);
  const [diffAgainst, setDiffAgainst] = useState("");

  const loadVersions = async () => {
    setError(null);
    try {
      const r = await api.get("/m5admin/content/course-versions");
      setVersions(r.data.versions || []);
    } catch (err) {
      setError(extractApiError(err, "Failed to load versions."));
    }
  };

  const loadDoc = async (v) => {
    setSelectedDoc(null); setPreviewData(null); setServerErrors([]);
    if (!v) return;
    try {
      const r = await api.get(`/m5admin/content/course-versions/${v}`);
      const doc = r.data.version;
      setSelectedDoc(doc);
      setCoursesJSON(JSON.stringify(doc.courses, null, 2));
      setFieldsJSON(JSON.stringify(doc.capstone_fields, null, 2));
      setNotes(doc.notes || "");
    } catch (err) {
      setError(extractApiError(err, "Failed to load that version."));
    }
  };

  useEffect(() => { loadVersions(); }, []);
  useEffect(() => { loadDoc(selectedVersion); }, [selectedVersion]);

  const selectVersion = (v) => setParams({ v });

  const parsedCourses = useMemo(() => {
    try { return { ok: true, value: JSON.parse(coursesJSON) }; }
    catch (e) { return { ok: false, err: e.message }; }
  }, [coursesJSON]);
  const parsedFields = useMemo(() => {
    try { return { ok: true, value: JSON.parse(fieldsJSON) }; }
    catch (e) { return { ok: false, err: e.message }; }
  }, [fieldsJSON]);

  useEffect(() => {
    if (!parsedCourses.ok) setJsonError(`courses: ${parsedCourses.err}`);
    else if (!parsedFields.ok) setJsonError(`capstone_fields: ${parsedFields.err}`);
    else setJsonError(null);
  }, [parsedCourses, parsedFields]);

  const createDraft = async () => {
    setBusy(true); setError(null);
    try {
      const based_on = selectedVersion || undefined;
      const r = await api.post("/m5admin/content/course-versions",
                                 { based_on });
      toast.success(`Draft ${r.data.version.version} created.`);
      await loadVersions();
      selectVersion(r.data.version.version);
    } catch (err) {
      toast.error(extractApiError(err, "Could not create a draft."));
    } finally { setBusy(false); }
  };

  const saveDraft = async () => {
    if (jsonError) { toast.error(jsonError); return; }
    setBusy(true); setServerErrors([]);
    try {
      const r = await api.put(
        `/m5admin/content/course-versions/${selectedDoc.version}`,
        {
          courses: parsedCourses.value,
          capstone_fields: parsedFields.value,
          notes,
        }
      );
      toast.success("Draft saved.");
      setSelectedDoc(r.data.version);
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d && Array.isArray(d.errors)) {
        setServerErrors(d.errors);
        toast.error("Validation failed — see errors below.");
      } else {
        toast.error(extractApiError(err, "Save failed."));
      }
    } finally { setBusy(false); }
  };

  const validateOnly = async () => {
    setBusy(true); setServerErrors([]);
    try {
      const r = await api.post(
        `/m5admin/content/course-versions/${selectedDoc.version}/validate`,
        {}
      );
      const errs = [...(r.data.structural_errors || []),
                    ...(r.data.id_stability_errors || [])];
      setServerErrors(errs);
      if (r.data.ok) toast.success("Validation passed.");
      else toast.error(`${errs.length} validation error(s).`);
    } catch (err) {
      toast.error(extractApiError(err, "Validation failed."));
    } finally { setBusy(false); }
  };

  const publish = async () => {
    setBusy(true); setServerErrors([]);
    try {
      const r = await api.post(
        `/m5admin/content/course-versions/${selectedDoc.version}/publish`,
        {}
      );
      toast.success(`Published ${r.data.version.version}.`);
      await loadVersions();
      await loadDoc(selectedDoc.version);
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d && Array.isArray(d.errors)) {
        setServerErrors(d.errors);
        toast.error("Publish blocked by validation.");
      } else {
        toast.error(extractApiError(err, "Publish failed."));
      }
    } finally { setBusy(false); }
  };

  const unpublish = async () => {
    if (!window.confirm(
      `Hide version ${selectedDoc.version} from new starts? ` +
      "Existing completions stay intact."
    )) return;
    setBusy(true);
    try {
      await api.post(
        `/m5admin/content/course-versions/${selectedDoc.version}/unpublish`,
        {}
      );
      toast.success("Unpublished.");
      await loadVersions();
      await loadDoc(selectedDoc.version);
    } catch (err) {
      toast.error(extractApiError(err, "Unpublish failed."));
    } finally { setBusy(false); }
  };

  const restore = async () => {
    setBusy(true);
    try {
      const r = await api.post(
        `/m5admin/content/course-versions/${selectedDoc.version}/restore`,
        {}
      );
      toast.success(`New draft ${r.data.version.version} created from ${selectedDoc.version}.`);
      await loadVersions();
      selectVersion(r.data.version.version);
    } catch (err) {
      toast.error(extractApiError(err, "Restore failed."));
    } finally { setBusy(false); }
  };

  const deleteDraft = async () => {
    if (!window.confirm(
      `Delete draft ${selectedDoc.version}? This cannot be undone.`
    )) return;
    setBusy(true);
    try {
      await api.delete(
        `/m5admin/content/course-versions/${selectedDoc.version}`
      );
      toast.success("Draft deleted.");
      await loadVersions();
      selectVersion(null);
    } catch (err) {
      toast.error(extractApiError(err, "Delete failed."));
    } finally { setBusy(false); }
  };

  const doPreview = async () => {
    if (!previewModuleId.trim()) return;
    try {
      const r = await api.get(
        `/m5admin/content/course-versions/${selectedDoc.version}/preview/module/${previewModuleId.trim()}`
      );
      setPreviewData(r.data);
    } catch (err) {
      toast.error(extractApiError(err, "Preview failed."));
    }
  };

  const doDiff = async () => {
    setDiffData(null);
    try {
      const qs = diffAgainst ? `?against=${encodeURIComponent(diffAgainst)}` : "";
      const r = await api.get(
        `/m5admin/content/course-versions/${selectedDoc.version}/diff${qs}`
      );
      setDiffData(r.data);
    } catch (err) {
      toast.error(extractApiError(err, "Diff failed."));
    }
  };

  const activeVersion = versions.find((v) => v.is_active)?.version;

  return (
    <AppShell>
      <div className="mx-auto max-w-6xl px-4 py-8">
        <Link
          to="/admin/m5"
          className="inline-flex items-center gap-2 text-sm text-fg-secondary hover:text-fg-primary"
          data-testid="content-back-link"
        >
          <ArrowLeft className="h-4 w-4" /> Back to admin
        </Link>
        <header className="mt-2 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-heading font-medium text-fg-primary" data-testid="content-title">
              Course content & pathway management
            </h1>
            <p className="mt-2 text-sm text-fg-secondary max-w-3xl">
              {HONEST_NOTE}
            </p>
          </div>
          <Button
            onClick={createDraft}
            disabled={busy}
            data-testid="content-new-draft-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> New draft
          </Button>
        </header>

        {error && (
          <Alert variant="destructive" className="mt-6">
            <AlertDescription data-testid="content-error">{error}</AlertDescription>
          </Alert>
        )}

        <section className="mt-8">
          <h2 className="text-xl font-heading font-medium text-fg-primary flex items-center gap-2">
            <FileEdit className="h-5 w-5" /> Versions
          </h2>
          <div className="mt-3 rounded-lg border border-border-subtle bg-bg-surface overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Version</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead>Based on</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Published</TableHead>
                  <TableHead className="text-right">Open</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {versions.map((v) => (
                  <TableRow
                    key={v.version}
                    data-testid={`content-row-${v.version}`}
                    className={selectedVersion === v.version ? "bg-bg-base" : ""}
                  >
                    <TableCell className="font-medium">
                      {v.version}
                      {v.is_active && (
                        <span
                          className="ml-2 text-[10px] uppercase tracking-wider text-progress-teal"
                          data-testid={`content-active-marker-${v.version}`}
                        >Active</span>
                      )}
                    </TableCell>
                    <TableCell><StateBadge state={v.state} /></TableCell>
                    <TableCell className="text-fg-secondary">{v.based_on || "—"}</TableCell>
                    <TableCell className="text-fg-secondary text-xs">
                      {v.created_at ? new Date(v.created_at).toLocaleString() : "—"}
                    </TableCell>
                    <TableCell className="text-fg-secondary text-xs">
                      {v.published_at ? new Date(v.published_at).toLocaleString() : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline" size="sm"
                        onClick={() => selectVersion(v.version)}
                        data-testid={`content-open-${v.version}`}
                      >Open</Button>
                    </TableCell>
                  </TableRow>
                ))}
                {versions.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center py-6 text-fg-secondary">No versions yet.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </section>

        {selectedDoc && (
          <section className="mt-8">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <h2 className="text-xl font-heading font-medium text-fg-primary" data-testid="content-selected-title">
                Version {selectedDoc.version}
              </h2>
              <StateBadge state={selectedDoc.state} />
              {selectedDoc.based_on && (
                <span className="text-xs text-fg-secondary">
                  based on {selectedDoc.based_on}
                </span>
              )}
              <div className="grow" />
              {selectedDoc.state === "draft" && (
                <>
                  <Button
                    onClick={validateOnly} variant="outline"
                    disabled={busy || !!jsonError}
                    data-testid="content-validate-btn"
                  >Validate</Button>
                  <Button
                    onClick={saveDraft}
                    disabled={busy || !!jsonError}
                    data-testid="content-save-btn"
                  >Save</Button>
                  <Button
                    onClick={publish} variant="default"
                    disabled={busy || !!jsonError}
                    data-testid="content-publish-btn"
                  ><Upload className="h-4 w-4 mr-1" /> Publish</Button>
                  <Button
                    onClick={deleteDraft} variant="destructive"
                    disabled={busy || !!selectedDoc.published_at}
                    data-testid="content-delete-btn"
                  ><Trash2 className="h-4 w-4 mr-1" /> Delete</Button>
                </>
              )}
              {selectedDoc.state === "published" && (
                <>
                  <Button
                    onClick={unpublish} variant="outline"
                    disabled={busy}
                    data-testid="content-unpublish-btn"
                  >Unpublish</Button>
                </>
              )}
              <Button
                onClick={restore} variant="outline" disabled={busy}
                data-testid="content-restore-btn"
              ><Undo2 className="h-4 w-4 mr-1" /> Restore as new draft</Button>
            </div>

            {selectedDoc.state !== "draft" && (
              <Alert className="mb-3">
                <AlertDescription>
                  This version is <b>{selectedDoc.state}</b> and cannot be
                  edited directly. Use <em>Restore as new draft</em> to make
                  changes.
                </AlertDescription>
              </Alert>
            )}

            {serverErrors.length > 0 && (
              <div className="mb-3 rounded-md border border-red-400/50 bg-red-50/30 p-3" data-testid="content-server-errors">
                <p className="text-sm font-medium text-red-700 flex items-center gap-2">
                  <ShieldAlert className="h-4 w-4" />
                  {serverErrors.length} validation error(s)
                </p>
                <ul className="mt-2 space-y-1 text-xs text-red-800 max-h-48 overflow-auto">
                  {serverErrors.map((e, i) => (
                    <li key={i}>
                      <b className="font-mono">{e.path || "bundle"}</b>: {e.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {jsonError && (
              <Alert variant="destructive" className="mb-3">
                <AlertDescription data-testid="content-json-error">
                  JSON parse error: {jsonError}
                </AlertDescription>
              </Alert>
            )}

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-xs uppercase tracking-wider text-fg-secondary">Courses (JSON)</label>
                <Textarea
                  value={coursesJSON}
                  onChange={(e) => setCoursesJSON(e.target.value)}
                  disabled={selectedDoc.state !== "draft"}
                  className="mt-1 font-mono text-xs h-96"
                  data-testid="content-courses-json"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-fg-secondary">Capstone fields (JSON)</label>
                <Textarea
                  value={fieldsJSON}
                  onChange={(e) => setFieldsJSON(e.target.value)}
                  disabled={selectedDoc.state !== "draft"}
                  className="mt-1 font-mono text-xs h-96"
                  data-testid="content-fields-json"
                />
              </div>
            </div>
            <div className="mt-3">
              <label className="text-xs uppercase tracking-wider text-fg-secondary">Notes</label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={selectedDoc.state !== "draft"}
                data-testid="content-notes"
                placeholder="Short change note visible to future admins."
              />
            </div>

            <div className="mt-6">
              <h3 className="text-lg font-heading font-medium text-fg-primary flex items-center gap-2">
                <FileEdit className="h-5 w-5" /> Diff against a published version
              </h3>
              <p className="text-xs text-fg-secondary mt-1">
                Compare this version against a published baseline before you
                publish. Shows additions, edits, removals, and deprecation
                toggles at every id level. Removing an id that has existing
                completions is still blocked at publish time.
              </p>
              <div className="mt-2 flex gap-2">
                <Input
                  value={diffAgainst}
                  onChange={(e) => setDiffAgainst(e.target.value)}
                  placeholder="baseline version (blank = latest published)"
                  data-testid="content-diff-against-input"
                />
                <Button onClick={doDiff} variant="outline" data-testid="content-diff-btn">
                  Compute diff
                </Button>
              </div>
              {diffData && (
                <div className="mt-4 rounded-lg border border-border-subtle bg-bg-surface p-4" data-testid="content-diff-panel">
                  <p className="text-xs text-fg-secondary">
                    <b className="text-fg-primary">{diffData.left.version}</b> ({diffData.left.state}) vs {" "}
                    <b className="text-fg-primary">{diffData.right.version}</b> ({diffData.right.state})
                  </p>
                  <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {Object.entries(diffData.diff.summary).map(([level, s]) => (
                      <div key={level} className="rounded border border-border-subtle p-2">
                        <p className="text-xs uppercase tracking-wider text-fg-secondary">{level}</p>
                        <p className="text-xs">
                          <span className="text-progress-teal">+{s.added} added</span> {" · "}
                          <span className="text-accent-warm">~{s.edited} edited</span> {" · "}
                          <span className="text-red-600">−{s.removed} removed</span>
                          {s.deprecation_toggled > 0 && (
                            <span className="text-fg-secondary"> {" · "}{s.deprecation_toggled} deprecation toggled</span>
                          )}
                        </p>
                      </div>
                    ))}
                  </div>
                  {(diffData.diff.modules.removed.length > 0
                     || diffData.diff.exercises.removed.length > 0
                     || diffData.diff.capstones.removed.length > 0) && (
                    <Alert variant="destructive" className="mt-3">
                      <AlertDescription>
                        This draft removes an id. Publish will be blocked if
                        any removed id has existing participant completions.
                        Mark it <code>deprecated: true</code> instead.
                      </AlertDescription>
                    </Alert>
                  )}
                  <details className="mt-3 text-xs">
                    <summary className="cursor-pointer text-fg-secondary">Raw diff</summary>
                    <pre className="mt-2 max-h-64 overflow-auto bg-bg-base p-2 rounded text-[10px]">
{JSON.stringify(diffData.diff, null, 2)}
                    </pre>
                  </details>
                </div>
              )}
            </div>

            <div className="mt-6">
              <h3 className="text-lg font-heading font-medium text-fg-primary flex items-center gap-2">
                <Eye className="h-5 w-5" /> Preview a module
              </h3>
              <p className="text-xs text-fg-secondary mt-1">
                Preview renders the module in the same shape participants
                see, marked with a "DRAFT PREVIEW" banner. Participants
                never see this content until you publish this version.
              </p>
              <div className="mt-2 flex gap-2">
                <Input
                  value={previewModuleId}
                  onChange={(e) => setPreviewModuleId(e.target.value)}
                  placeholder="module id, e.g., found_m1"
                  data-testid="content-preview-module-input"
                />
                <Button onClick={doPreview} data-testid="content-preview-btn">Preview</Button>
              </div>
              {previewData && (
                <div className="mt-4 rounded-lg border-2 border-accent-warm bg-accent-warm/5 p-4" data-testid="content-preview-panel">
                  <p className="text-xs uppercase tracking-wider text-accent-warm font-medium mb-2">
                    {previewData.preview_banner}
                  </p>
                  <h4 className="text-xl font-heading font-medium text-fg-primary">
                    {previewData.module?.title}
                  </h4>
                  <p className="text-sm text-fg-secondary mt-1">{previewData.module?.description}</p>
                  <div className="mt-4 space-y-3">
                    {(previewData.module?.blocks || []).map((b, i) => (
                      <div key={i} className="text-xs">
                        <span className="inline-block rounded bg-bg-base px-2 py-0.5 text-[10px] uppercase tracking-wider text-fg-secondary mr-2">
                          {b.kind}
                        </span>
                        <span className="text-fg-primary">
                          {typeof b.content === "string"
                            ? b.content.slice(0, 200)
                            : JSON.stringify(b.content).slice(0, 200)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>
        )}
      </div>
    </AppShell>
  );
}
